import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, AudioLines, Instagram, Music2, Music4 } from "lucide-react";
import { fadeUp } from "@/lib/motion";
import SpinRating from "@/components/SpinRating";
import StoryShare from "@/components/StoryShare";
import CommentSection from "@/components/CommentSection";
import VoteBanner from "@/components/VoteBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RhythmReviewPage() {
  const { slug } = useParams();

  const { data: items = [] } = useQuery({
    queryKey: ["rhythm"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/rhythm`);
        return data || [];
      } catch {
        return [];
      }
    },
  });

  const { data: art } = useQuery({
    queryKey: ["rhythm-artwork", slug],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/rhythm/${slug}/artwork`);
        return data;
      } catch {
        return null;
      }
    },
    enabled: !!slug,
  });

  const post = items.find((i) => i.slug === slug);
  const others = items.filter((i) => i.kind === "review" && i.slug !== slug).slice(0, 2);

  if (items.length && !post) {
    return (
      <div className="min-h-screen bg-[#0B0A0F] px-5 py-32 text-center text-[#F3F1ED]">
        <p className="font-garamond text-2xl italic text-white/70">
          That review decoded itself away.
        </p>
        <Link
          data-testid="rhythm-review-back-missing"
          to="/rhythm"
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white/80 hover:border-[#F3F1ED] hover:text-[#F3F1ED]"
        >
          <ArrowLeft className="h-4 w-4" /> The Rhythm Report
        </Link>
      </div>
    );
  }

  if (!post) {
    return <div className="min-h-screen bg-[#0B0A0F]" />;
  }

  const paragraphs = (post.body || "").split("\n\n").filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0B0A0F] font-rhythm text-[#F3F1ED]">
      <VoteBanner />
      <div className="border-b border-black/30 bg-[#F3F1ED] text-[#0B0A0F]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-1.5 font-rhythm-mono text-[10px] uppercase tracking-[0.18em]">
          <span data-testid="rhythm-review-issue">The Rhythm Report — Vol. 1 · Issue 03</span>
          <span className="hidden sm:inline">October 2026</span>
          <span>$4.95 US</span>
        </div>
      </div>

      <article className="mx-auto max-w-5xl px-5 py-10 sm:py-14">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            data-testid="rhythm-review-back"
            to="/rhythm"
            className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-[#F3F1ED]"
          >
            <ArrowLeft className="h-4 w-4" /> The Rhythm Report
          </Link>
          <StoryShare path={`/rhythm/${post.slug}/story-card`} title={`${post.artist} — ${post.title}`} />
        </div>

        <motion.header
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mt-8 border-b-4 border-[#F3F1ED] pb-8"
        >
          <p className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#F3F1ED]">
            {post.week} · {post.genre}
          </p>
          <h1 className="mt-4 font-rhythm-display text-5xl uppercase leading-[0.92] tracking-tight sm:text-6xl lg:text-7xl">
            {post.artist}
          </h1>
          <p className="mt-4 font-garamond text-2xl italic text-white/85 sm:text-3xl">
            {post.title}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 font-rhythm-mono text-[10px] uppercase tracking-[0.18em] text-white/55">
            {post.archived && (
              <span
                data-testid="rhythm-review-archive-badge"
                className="rounded-full border border-[#F3F1ED]/40 px-3 py-1 text-[#F3F1ED]"
              >
                From the Archive
              </span>
            )}
            <span className="text-[#F3F1ED]">By Morgan Williams</span>
            <span>
              {post.ptype} · {post.year}
            </span>
            {post.score && <SpinRating score={post.score} size={20} mono />}
          </div>
        </motion.header>

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rhythm-frame relative overflow-hidden rounded-[26px]"
          >
            {parseFloat(post.score) / 2 >= 4.75 && (
              <div
                data-testid="rhythm-review-classic-stamp"
                className="absolute right-6 top-6 z-10 -rotate-6 border-4 border-[#F3F1ED] bg-[#0B0A0F]/85 px-4 py-1.5 font-rhythm-display text-xl uppercase tracking-[0.1em] text-[#F3F1ED] backdrop-blur"
              >
                Classic
              </div>
            )}
            {art?.artwork_url ? (
              <a
                data-testid="rhythm-record-art-link"
                href={art.apple_url || art.spotify_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  data-testid="rhythm-record-artwork"
                  src={art.artwork_url}
                  alt={`${art.album_name || post.title} — official cover art`}
                  className="aspect-square w-full rounded-[18px] object-cover"
                />
              </a>
            ) : (
              <img
                data-testid="rhythm-review-artwork"
                src={post.image_url}
                alt={`${post.artist} — ${post.title}`}
                className="aspect-square w-full rounded-[18px] object-cover"
              />
            )}
          </motion.div>
          {art?.artwork_url && (
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="rhythm-frame relative overflow-hidden rounded-[26px]"
            >
              <img
                data-testid="rhythm-review-artwork"
                src={post.image_url}
                alt={`The Rhythm Report cover — ${post.artist}`}
                className="aspect-square w-full rounded-[18px] object-cover"
              />
            </motion.div>
          )}
        </div>

        {art?.found && (
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="mt-5 flex flex-wrap items-center justify-between gap-4 font-rhythm-mono text-[10px] uppercase tracking-[0.18em] text-white/50"
          >
            <span data-testid="rhythm-record-caption">
              THE RECORD{art?.album_name ? ` — ${art.album_name}` : ""}
              {art?.release_date ? ` · OUT ${art.release_date}` : ""}
            </span>
            <span className="flex flex-wrap items-center gap-3">
              {art?.apple_url && (
                <a
                  data-testid="rhythm-record-apple"
                  href={art.apple_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-white/20 px-4 py-2 transition-colors hover:border-[#F3F1ED] hover:text-[#F3F1ED]"
                >
                  Apple Music
                </a>
              )}
              {art?.spotify_url && (
                <a
                  data-testid="rhythm-record-spotify"
                  href={art.spotify_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-white/20 px-4 py-2 transition-colors hover:border-[#F3F1ED] hover:text-[#F3F1ED]"
                >
                  Spotify
                </a>
              )}
            </span>
          </motion.div>
        )}

        {post.press_photo_url && (
          <motion.figure
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-10"
          >
            <div className="rhythm-frame overflow-hidden rounded-[26px]">
              <img
                data-testid="rhythm-review-press-photo"
                src={post.press_photo_url}
                alt={`${post.artist} — licensed press photo`}
                className="h-[420px] w-full object-cover sm:h-[520px]"
              />
            </div>
            <figcaption className="mt-3 flex flex-wrap items-center justify-between gap-3 font-rhythm-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
              <span data-testid="rhythm-review-press-credit">{post.press_photo_credit}</span>
              <span>Licensed Media</span>
            </figcaption>
          </motion.figure>
        )}

        <div data-testid="rhythm-review-body" className="mag-columns mt-10">
          {paragraphs.map((p, i) => (
            <motion.p
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              className={`mb-6 text-base leading-relaxed text-white/80 sm:text-lg ${
                i === 0 ? "drop-cap" : ""
              }`}
            >
              {p}
            </motion.p>
          ))}
        </div>

        {post.verdict && (
          <motion.blockquote
            data-testid="rhythm-review-verdict"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="mt-8 border-y-4 border-[#F3F1ED] px-2 py-8 sm:px-8"
          >
            <p className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#F3F1ED]">
              Morgan's verdict
            </p>
            <p className="mt-4 font-garamond text-2xl italic leading-snug text-[#F3F1ED] sm:text-3xl">
              {post.verdict}
            </p>
          </motion.blockquote>
        )}

        {post.links && (
          <motion.div
            data-testid="rhythm-connect"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="mt-10 border-2 border-[#F3F1ED] p-8 text-center sm:p-10"
          >
            <p className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#F3F1ED]">
              Connect with {post.artist}
            </p>
            <h3 className="mt-3 font-rhythm-display text-2xl uppercase tracking-tight sm:text-3xl">
              Hear the music. Follow the move.
            </h3>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              {post.links.spotify && (
                <a
                  data-testid="rhythm-connect-spotify"
                  href={post.links.spotify}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 rounded-full bg-[#F3F1ED] px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0B0A0F] transition-transform duration-300 hover:scale-[1.04]"
                >
                  <AudioLines className="h-4 w-4" /> Spotify
                </a>
              )}
              {post.links.apple_music && (
                <a
                  data-testid="rhythm-connect-apple"
                  href={post.links.apple_music}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 rounded-full border-2 border-[#F3F1ED] px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F3F1ED] transition-colors hover:bg-[#F3F1ED] hover:text-[#0B0A0F]"
                >
                  <Music2 className="h-4 w-4" /> Apple Music
                </a>
              )}
              {post.links.tiktok && (
                <a
                  data-testid="rhythm-connect-tiktok"
                  href={post.links.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 rounded-full border-2 border-[#F3F1ED] px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F3F1ED] transition-colors hover:bg-[#F3F1ED] hover:text-[#0B0A0F]"
                >
                  <Music4 className="h-4 w-4" /> TikTok
                </a>
              )}
              {post.links.instagram && (
                <a
                  data-testid="rhythm-connect-instagram"
                  href={post.links.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white/80 transition-colors hover:border-[#F3F1ED] hover:text-[#F3F1ED]"
                >
                  <Instagram className="h-4 w-4" /> Instagram
                </a>
              )}
            </div>
          </motion.div>
        )}

        <CommentSection
          articleType="rhythm"
          slug={post.slug}
          accent="#F3F1ED"
          onAccent="#0B0A0F"
        />

        {others.length > 0 && (
          <div className="mt-16 border-t border-white/10 pt-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-white/50">
              More from the report
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  data-testid={`rhythm-review-next-${o.slug}`}
                  to={`/rhythm/${o.slug}`}
                  className="rhythm-card group rounded-2xl p-6 transition-transform duration-300 hover:translate-x-1"
                >
                  <p className="font-rhythm-display text-base font-bold uppercase">{o.artist}</p>
                  <p className="mt-1 font-garamond text-lg italic text-white/75">{o.title}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F3F1ED]">
                    Read review <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
